import { Component, Input, Output, EventEmitter, forwardRef, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-sl-input',
  standalone: true,
  imports: [NgIf],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="sl-input-wrapper">
      <label *ngIf="label" class="block text-sm font-medium text-gray-700 mb-2">
        {{ label }}
        <span *ngIf="required" class="text-red-500 ml-1">*</span>
      </label>

      <sl-input
        #inputElement
        [type]="type"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [readonly]="readonly"
        [size]="size"
        [clearable]="clearable"
        [password-toggle]="passwordToggle"
        [min]="min"
        [max]="max"
        [step]="step"
        [minlength]="minlength"
        [maxlength]="maxlength"
        [pattern]="pattern"
        (sl-input)="onInput($event)"
        (sl-change)="onChange($event)"
        (sl-blur)="onBlur()"
        (sl-focus)="onFocus()"
        [class.invalid]="isFieldInvalid()">
      </sl-input>

      <div *ngIf="getValidationError()" class="mt-1 text-sm text-red-600">
        {{ getValidationError() }}
      </div>

      <div *ngIf="showCharCount && maxlength" class="mt-1 text-xs text-gray-500 text-right">
        {{ currentLength }}/{{ maxlength }}
      </div>
    </div>
  `,
  styleUrls: ['./sl-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SlInputComponent),
      multi: true
    }
  ]
})
export class SlInputComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef;

  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() clearable: boolean = false;
  @Input() passwordToggle: boolean = false;
  @Input() invalid: boolean = false;
  @Input() helpText: string = '';
  @Input() showCharCount: boolean = false;
  @Input() formControl: any; // FormControl for validation
  @Input() fieldName: string = '';

  // Number input specific
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() step: number | undefined;

  // Text input specific
  @Input() minlength: number | undefined;
  @Input() maxlength: number | undefined;
  @Input() pattern: string | undefined;

  @Output() inputChange = new EventEmitter<string>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() focusEvent = new EventEmitter<void>();
  @Output() blurEvent = new EventEmitter<void>();

  private _value: string = '';
  private onChangeCallback = (value: string) => {};
  private onTouchedCallback = () => {};

  get currentLength(): number {
    return this._value?.length || 0;
  }

  ngAfterViewInit(): void {
    // Set initial value after view init
    if (this._value && this.inputElement) {
      setTimeout(() => {
        this.inputElement.nativeElement.value = this._value;
      });
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this._value = value || '';

    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.value = this._value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: any): void {
    const value = event.target.value;

    // Check max length if specified
    if (this.maxlength && value.length > this.maxlength) {
      return; // Don't update if exceeds max length
    }

    this._value = value;
    this.onChangeCallback(value);
    this.inputChange.emit(value);
  }

  onChange(event: any): void {
    const value = event.target.value;
    this._value = value;
    this.valueChange.emit(value);
  }

  onBlur(): void {
    this.onTouchedCallback();
    this.blurEvent.emit();

    // Mark field as touched for validation display
    if (this.formControl) {
      this.formControl.markAsTouched();
    }
  }

  onFocus(): void {
    this.focusEvent.emit();
  }

  // Helper methods
  getValue(): string {
    return this._value;
  }

  focus(): void {
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  clear(): void {
    this._value = '';
    this.onChangeCallback('');
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.value = '';
    }
  }

  select(): void {
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.select();
    }
  }

  // Validation methods
  getValidationError(): string {
    if (!this.formControl || !this.formControl.errors || !this.formControl.touched) {
      return '';
    }

    const errors = this.formControl.errors;

    if (errors['required']) {
      return `${this.getFieldDisplayName()} is required`;
    }

    if (errors['email']) {
      return 'Please enter a valid email address';
    }

    if (errors['pattern']) {
      if (this.type === 'tel') {
        return 'Please enter a valid UK phone number (e.g., +44 7700 900123)';
      }
      if (this.fieldName?.toLowerCase().includes('postcode')) {
        return 'Please enter a valid UK postcode (e.g., SW1A 1AA)';
      }
      if (this.fieldName?.toLowerCase().includes('nationalinsurance')) {
        return 'Please enter a valid National Insurance number (e.g., AB123456C)';
      }
      if (this.fieldName?.toLowerCase().includes('sortcode')) {
        return 'Please enter a valid sort code (e.g., 12-34-56)';
      }
      return `Please enter a valid ${this.getFieldDisplayName().toLowerCase()}`;
    }

    if (errors['minlength']) {
      return `${this.getFieldDisplayName()} must be at least ${errors['minlength'].requiredLength} characters`;
    }

    if (errors['maxlength']) {
      return `${this.getFieldDisplayName()} cannot exceed ${errors['maxlength'].requiredLength} characters`;
    }

    if (errors['min']) {
      return `${this.getFieldDisplayName()} must be greater than ${errors['min'].min}`;
    }

    if (errors['max']) {
      return `${this.getFieldDisplayName()} must be less than ${errors['max'].max}`;
    }

    // Generic error message
    return `Please enter a valid ${this.getFieldDisplayName().toLowerCase()}`;
  }

  private getFieldDisplayName(): string {
    if (this.label) {
      return this.label.replace(' *', '');
    }

    if (this.fieldName) {
      // Convert camelCase to readable format
      return this.fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    }

    return 'Field';
  }

  isFieldInvalid(): boolean {
    return !!(this.formControl && this.formControl.invalid && this.formControl.touched);
  }
}
